import { useSupabaseQuery } from './useSupabaseQuery';
import type { ConversationMessage, ToolEvent } from '../types/database';

export function useConversationMessages(
	userId: string | undefined,
	opts?: { limit?: number }
) {
	const limit = opts?.limit ?? 200;
	return useSupabaseQuery<ConversationMessage>(
		(client) =>
			client
				.from('conversation_messages')
				.select('*')
				.eq('user_id', userId ?? '')
				.order('created_at', { ascending: true })
				.limit(limit),
		[userId, limit]
	);
}

export function useToolEvents(userId: string | undefined) {
	return useSupabaseQuery<ToolEvent>(
		(client) =>
			client
				.from('tool_events')
				.select('*')
				.eq('user_id', userId ?? '')
				.order('created_at', { ascending: true }),
		[userId]
	);
}

/**
 * Get users with most recent message info for the chats list.
 * Fetches the latest 1000 messages and groups client-side.
 * Supabase JS doesn't support DISTINCT ON, so we fetch a generous window
 * and deduplicate in the consumer. Works well for dashboards with <100 users.
 */
export function useChatUsers() {
	// We query conversation_messages grouped by user with latest message
	// Supabase doesn't support GROUP BY directly, so we get distinct users from messages
	// and join with users table
	return useSupabaseQuery<{
		user_id: string;
		content: string | null;
		created_at: string;
		direction: string;
		users: { id: string; display_name: string | null; phone_number: string } | null;
	}>(
		(client) =>
			client
				.from('conversation_messages')
				.select('user_id, content, created_at, direction, users(id, display_name, phone_number)')
				.order('created_at', { ascending: false })
				.limit(1000),
		[]
	);
}
